import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { APP_ID, LOCALE_ID, enableProdMode, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_NATIVE_DATE_FORMATS, MatDateFormats, MatNativeDateModule } from '@angular/material/core';

import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import 'hammerjs';
import { AppComponent } from './app/components/app/app.component';
import { AppService } from './app/components/app/app.service';
import { ErrorInterceptor } from './app/shared/interceptors/error.interceptor';
import { JwtInterceptor } from './app/shared/interceptors/jwt.interceptor';
import { CommonService } from './app/shared/services/common.service';
import { environment } from './environments/environment';

import { Platform } from '@angular/cdk/platform';
import { provideRouter } from '@angular/router';
import '@progress/kendo-angular-intl/locales/en-AU/all';
import '@progress/kendo-angular-intl/locales/en-GB/all';
import '@progress/kendo-angular-intl/locales/en/all';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { QueryClient, provideTanStackQuery } from '@tanstack/angular-query-experimental';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import { appRoutes } from './app/components/app/app.routing';
import { CustomDateAdapter } from './app/custom-date-adapter';
import { ThemeService } from './app/shared/services/theme.service';

export const CURRENT_LOCALE = 'current-locale';
export const CURRENT_LOCALE_NAME = 'current-locale-name';

export const CUSTOM_DATE_FORMATS: MatDateFormats = {
  ...MAT_NATIVE_DATE_FORMATS,
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    } as Intl.DateTimeFormatOptions,
  }
};


// PRE-APPLY THEME BEFORE ANGULAR BOOTS (prevents FOUC/wrong CSS on restore)
(function preApplyTheme()
{
  try
  {
    const raw = (localStorage.getItem('cb-theme') as 'dark' | 'normal' | 'light' | null) ?? 'normal';
    const dark = raw === 'dark';
    const value = dark ? 'dark' : 'light';
    const html = document.documentElement;
    const body = document.body;
    html.setAttribute('data-theme', value);
    body.setAttribute('data-theme', value);
    (html.style as any).colorScheme = value;
    (body.style as any).colorScheme = value;

    const link = document.getElementById('kendo-theme') as HTMLLinkElement | null;
    if (link)
    {
      const base = 'assets/kendo/';
      const prefer = dark ? 'material-2-dark.css' : 'material-2.css';
      // Set preferred Kendo stylesheet eagerly
      link.href = base + prefer;
    }
  } catch { /* ignore */ }
})();

export function getBaseUrl()
{
  return document.getElementsByTagName('base')[0].href;
}

const appProviders = [
  { provide: APP_ID, useValue: 'ng-cli-universa' },
  { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] as any[] },
  importProvidersFrom(NotificationModule, MatNativeDateModule, BrowserModule),
  provideLottieOptions({ player: () => player }),
  provideAnimations(),
  provideHttpClient(withFetch(), withInterceptors([JwtInterceptor, ErrorInterceptor])),
  provideTanStackQuery(new QueryClient()),
  provideRouter(appRoutes),
  { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  {
    provide: DateAdapter, useClass: CustomDateAdapter,
    deps: [MAT_DATE_LOCALE, Platform]
  },
  provideAppInitializer(() =>
  {
    const initFn = ((appService: AppService) =>
    {
      return () =>
      {
        return appService.loadApplication().then(() =>
        {

        });
      }
    })(inject(AppService));
    return initFn();
  }),
  provideAppInitializer(() =>
  {
    inject(ThemeService);
  }),
  {
    provide: LOCALE_ID,
    deps: [CommonService],
    useFactory: (commonService: CommonService) => commonService.getLocaleName() //returns locale string
  },
  {
    provide: MAT_DATE_LOCALE,
    deps: [CommonService],
    useFactory: (commonService: CommonService) => commonService.getLocaleName() //returns locale string
  },
  //{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill' } },
  //CldrIntlService,
  //{
  //  provide: IntlService,
  //  useClass: CustomKendoIntlService
  //}
  // environment.production ? [] : provideQueryDevTools({ initialIsOpen: true  })
];

if (environment.production)
{
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: appProviders
})
  .catch((err: any) => console.log(err));
